import { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { RefreshIcon, ClipboardCopyIcon, ChevronRightIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'
import { areaTone, areaIcon, operatorHeadline, isRetryableFlag, retryBudget } from '../utils/fulfillmentErrorPresentation'

// Seconds left until the instant the server sent. Returns null if no date came
// —then the fixed duration rules— and never negatives: once the moment passes, the wait is over.
const secondsUntil = iso => {
  if (!iso) return null
  const remaining = Math.ceil((new Date(iso).getTime() - Date.now()) / 1000)
  return Number.isFinite(remaining) ? Math.max(remaining, 0) : null
}

export const FulfillmentErrorModal = ({
  open,
  setOpen,
  errorCode,
  errorCategory,
  responsibleArea,
  flagType,
  errorMessage,
  // The backend's own text for this one error (fulfillment_error_summary), which says more than the
  // code's generic one: e.g. that every carrier was tried and none covers the postal code.
  errorSummary,
  errorDetail,
  retryAfterSeconds,
  nextCheckAt,
  retryCount,
  maxRetries,
  onRetry,
  onWaitElapsed,
  onReportToArea,
  storeName,
  orderNumber,
  orderId,
  tote,
  carrierName,
  destination,
  weight,
}) => {
  const { i18n } = useTranslation()
  const closeButtonRef = useRef()
  const [showTechnical, setShowTechnical] = useState(false)
  const [copied, setCopied] = useState(false)
  // Counts down the wait the backend measured for this code. Only codes with a real
  // basis carry one; the rest arrive undefined and the card shows no time at all.
  const [secondsLeft, setSecondsLeft] = useState(retryAfterSeconds ?? null)
  // While the clock ran, the server cycle re-triggered whatever was missing and only it
  // knows how that ended. On reaching zero we ask it, and until it answers the button
  // stays locked: enabling it just because time passed would hand the operator back the
  // click against the same error as a minute ago.
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    const initial = secondsUntil(nextCheckAt) ?? retryAfterSeconds ?? null
    if (!open || !initial) {
      setSecondsLeft(initial)
      return undefined
    }
    setSecondsLeft(initial)
    const timer = setInterval(() => {
      setSecondsLeft(prev => (prev === null || prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(timer)
    // The clock is derived from nextCheckAt —the instant the server will check again—
    // and not from a fixed duration. It used to be reset by retryCount, which worked while each
    // round opened a new record; now the cycle reuses the same record and what changes
    // is the date, so hanging off retryCount would leave the clock stuck at zero with the
    // button enabled mid-wait. As a bonus this survives a page reload, which
    // used to restart the full wait from the beginning.
  }, [open, retryAfterSeconds, nextCheckAt])

  useEffect(() => {
    if (!open || secondsLeft !== 0 || !onWaitElapsed) return undefined

    let alive = true
    setChecking(true)
    Promise.resolve(onWaitElapsed()).finally(() => {
      if (alive) setChecking(false)
    })
    return () => {
      alive = false
    }
  }, [open, secondsLeft, onWaitElapsed])

  // Once the budget is spent, the error already arrived as tech + escalate: offering the button again
  // would invite the hammering the budget exists to cut off.
  const budget = isRetryableFlag(flagType) || flagType === 'escalate' ? retryBudget({ retryCount, maxRetries }) : null
  const canRetry = isRetryableFlag(flagType) && !(budget && budget.exhausted)
  const waiting = canRetry && ((secondsLeft !== null && secondsLeft > 0) || checking)

  const tone = areaTone(responsibleArea)
  const AreaIcon = areaIcon(responsibleArea)

  const explanation = errorSummary || i18n.t(`fulfillment_errors.error_codes.${errorCode}`, { defaultValue: errorMessage || errorCode })
  const areaLabel = responsibleArea ? i18n.t(`fulfillment_errors.responsible_area.${responsibleArea}`, { defaultValue: responsibleArea }) : null
  const headline = operatorHeadline(i18n, { flagType, waiting, areaLabel })
  const categoryLabel = errorCategory ? i18n.t(`fulfillment_errors.category.${errorCategory}`, { defaultValue: errorCategory }) : null

  // Every field the card can show; the strip renders only the ones that carry a value, so
  // a Mexican order with no tote does not leave an empty column behind.
  const facts = [
    { key: 'order', label: i18n.t('fulfillment_errors.card.order'), value: orderNumber ? `#${orderNumber}` : null },
    { key: 'tote', label: i18n.t('fulfillment_errors.card.tote'), value: tote },
    { key: 'carrier', label: i18n.t('fulfillment_errors.card.carrier'), value: carrierName },
    { key: 'destination', label: i18n.t('fulfillment_errors.card.destination'), value: destination },
    { key: 'weight', label: i18n.t('fulfillment_errors.card.weight'), value: weight },
  ].filter(fact => fact.value)

  // The column width is split among the facts that ARE present. With a fixed grid of
  // five, three facts took three fifths and the order number —the fact the
  // operator reads out when reporting— came out truncated.
  const COLUMNS = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5' }
  const columns = COLUMNS[facts.length] || 'sm:grid-cols-4'

  // What a person pastes into the channel where the owning area is listening. Plain text
  // on purpose: it has to survive Slack, WhatsApp and a ticket form unchanged.
  // The internal id goes ONLY here, not in the facts strip: it is no use to the operator —they read
  // out the order number— but it saves the search for whoever receives the escalation, because it is
  // what opens the order in the WMS and what gets queried in the database. It sits next to the order number
  // so they read as a pair and nobody confuses which is which.
  const buildReport = () => {
    const lines = [
      `${i18n.t('fulfillment_errors.card.report_title')}: ${errorCode}`,
      areaLabel ? `${i18n.t('fulfillment_errors.error_modal.responsible')}: ${areaLabel}` : null,
      // High up, next to the area: whoever receives this in their channel serves several brands, and "which
      // store is it?" is the first question they would ask. It goes only in the report, not in the facts
      // strip: that one splits the width among the fields present and a sixth would narrow them
      // all, starting with the order number, which is the one the operator reads out.
      storeName ? `${i18n.t('fulfillment_errors.card.store')}: ${storeName}` : null,
      ...facts.map(fact => `${fact.label}: ${fact.value}`),
      orderId ? `${i18n.t('fulfillment_errors.card.order_id')}: ${orderId}` : null,
      `${i18n.t('fulfillment_errors.card.detail')}: ${errorDetail || errorMessage || explanation}`,
    ]
    return lines.filter(Boolean).join('\n')
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildReport())
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch (e) {
      // Clipboard access is denied on an insecure origin and in some locked-down
      // browsers. Open the technical panel so the operator can still select the text
      // by hand, rather than leaving the button looking like it did nothing.
      setShowTechnical(true)
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" static className="fixed z-10 inset-0 overflow-y-auto" initialFocus={closeButtonRef} open={open} onClose={() => setOpen(false)}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="flex">
                {/* Owner rail: the card's colour IS the owner, read before any text. */}
                <div className={`w-1.5 flex-shrink-0 ${tone.rail}`} aria-hidden="true" />

                <div className="flex-1 min-w-0">
                  {/* Headline: what to do now, not the word "Error" */}
                  <div className="px-6 pt-6 pb-5 flex items-start gap-4">
                    <div className={`rounded-lg p-2.5 flex-shrink-0 ${tone.tile}`}>
                      <AreaIcon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900 leading-tight">
                        {headline}
                      </Dialog.Title>
                      <p className="mt-1.5 text-base text-gray-600 leading-snug">{explanation}</p>
                      {/* The current instruction goes in bold: the code's text still
                                                says "retry in a few moments" and, once the budget is spent, that
                                                is no longer the instruction. */}
                      {budget && budget.exhausted ? (
                        <p className="mt-2 text-sm font-semibold text-gray-900">{i18n.t('fulfillment_errors.card.attempts_exhausted')}</p>
                      ) : canRetry && retryAfterSeconds ? (
                        <p className="mt-2 text-sm font-semibold text-gray-900">
                          {checking
                            ? i18n.t('fulfillment_errors.card.checking')
                            : waiting
                              ? i18n.t('fulfillment_errors.card.wait_countdown', { seconds: secondsLeft })
                              : i18n.t('fulfillment_errors.card.wait_elapsed')}
                        </p>
                      ) : null}
                      {budget && budget.attempt > 1 ? (
                        <p className="mt-1 text-sm text-gray-600">
                          {i18n.t('fulfillment_errors.card.attempt', { attempt: budget.attempt, total: budget.total })}
                        </p>
                      ) : null}
                    </div>
                    {areaLabel && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider flex-shrink-0 ${tone.chip}`}
                      >
                        <span className={`h-2 w-2 rounded-full ${tone.dot}`} aria-hidden="true" />
                        {areaLabel}
                      </span>
                    )}
                  </div>

                  {/* The order at a glance — what the operator reads out when reporting */}
                  {facts.length > 0 && (
                    <div className={`border-t border-gray-200 grid grid-cols-2 ${columns} divide-x divide-gray-200 ${tone.strip}`}>
                      {facts.map(fact => (
                        <div key={fact.key} className="px-4 py-3 min-w-0">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{fact.label}</p>
                          <p className="mt-0.5 font-mono text-sm text-gray-900 break-words" title={fact.value}>
                            {fact.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t border-gray-200 px-6 py-4 flex flex-wrap items-center gap-3">
                    {/* The retry button alternates between three texts of very different length
                        —"Reintentar", "Reintenta en 118 s", "Comprobando…"— and on top of that the counter
                        loses a digit when it drops below 100 and 10. Without a reserved width the button
                        shrinks and stretches every second, and the buttons next to it dance along with
                        it. min-w pins it to the longest text and justify-center keeps the
                        content centred while it changes; tabular-nums avoids the jitter between
                        digits of different width. */}
                    {canRetry && onRetry && (
                      <button
                        type="button"
                        disabled={waiting}
                        className="inline-flex items-center justify-center min-w-15 tabular-nums rounded-md border border-transparent shadow-sm px-4 py-3 bg-gray-900 text-sm font-semibold text-white hover:bg-gray-700 active:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        onClick={() => {
                          setOpen(false)
                          onRetry()
                        }}
                      >
                        <RefreshIcon className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true" />
                        {checking
                          ? i18n.t('fulfillment_errors.card.checking_short')
                          : waiting
                            ? i18n.t('fulfillment_errors.card.retry_in', { seconds: secondsLeft })
                            : i18n.t('fulfillment_errors.error_modal.retry')}
                      </button>
                    )}
                    {onReportToArea && areaLabel && (
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md border border-gray-300 shadow-sm px-4 py-3 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                        onClick={() => onReportToArea(buildReport())}
                      >
                        {i18n.t('fulfillment_errors.card.report_to', { area: areaLabel })}
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-gray-300 shadow-sm px-4 py-3 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                      onClick={handleCopy}
                    >
                      <ClipboardCopyIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                      {copied ? i18n.t('fulfillment_errors.card.copied') : i18n.t('fulfillment_errors.card.copy_report')}
                    </button>
                    <button
                      type="button"
                      className="ml-auto inline-flex items-center rounded-md px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 active:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                      onClick={() => setOpen(false)}
                      ref={closeButtonRef}
                    >
                      {i18n.t('fulfillment_errors.error_modal.close')}
                    </button>
                  </div>

                  {/* Technical detail — collapsed, because the operator does not need it
                                        and whoever they escalate to does. */}
                  <div className="border-t border-gray-200">
                    <button
                      type="button"
                      className="w-full px-6 py-4 flex items-center gap-2 text-left text-xs font-medium text-gray-600 hover:text-gray-900 active:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400"
                      aria-expanded={showTechnical}
                      onClick={() => setShowTechnical(v => !v)}
                    >
                      <ChevronRightIcon className={`h-4 w-4 transition-transform ${showTechnical ? 'transform rotate-90' : ''}`} aria-hidden="true" />
                      {i18n.t('fulfillment_errors.card.technical_detail')}
                    </button>
                    {showTechnical && (
                      <div className="px-6 pb-5 space-y-3">
                        <div className="flex flex-wrap gap-x-8 gap-y-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{i18n.t('fulfillment_errors.history.code')}</p>
                            <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border">{errorCode}</span>
                          </div>
                          {categoryLabel && (
                            <div>
                              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{i18n.t('fulfillment_errors.history.category')}</p>
                              <p className="text-xs text-gray-700 mt-1">{categoryLabel}</p>
                            </div>
                          )}
                        </div>
                        {/* What the external API answered, word for word. Whoever receives the
                            escalation needs that string and not our summary: the SEFAZ reason
                            or the carrier code live there and nowhere else. */}
                        {(errorDetail || errorMessage) && (
                          <pre className="text-xs font-mono text-gray-600 bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                            {errorDetail || errorMessage}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
